import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';

/*
 * App-wide light/dark mode. This lives ABOVE the anvil2 `AnvilProvider` (and therefore above the
 * react-ioc DI container) because the mode must be fed to `AnvilProvider.themeData.mode` so that the
 * whole anvil tree — including portaled overlays (menus, toasts, dialogs) — inherits the design-token
 * mode. A plain React context (rather than a DI store) is used precisely so it can sit at that level;
 * the Header consumes it via `useThemeMode`, crossing the DI boundary without issue.
 */
export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'org-hub:theme-mode';

interface ThemeModeContextValue {
    mode: ThemeMode;
    toggleMode: () => void;
    setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

// Persisted choice wins; otherwise fall back to the OS preference so first-time users get a sensible mode.
const getInitialMode = (): ThemeMode => {
    if (typeof window === 'undefined') {
        return 'light';
    }

    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
        return stored;
    }

    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const ThemeModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [mode, setMode] = useState<ThemeMode>(getInitialMode);

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // Ignore storage failures (private mode, quota): the mode still applies for the session.
        }
    }, [mode]);

    const toggleMode = useCallback(() => {
        setMode(prev => (prev === 'dark' ? 'light' : 'dark'));
    }, []);

    const value = useMemo<ThemeModeContextValue>(
        () => ({ mode, toggleMode, setMode }),
        [mode, toggleMode]
    );

    return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

export const useThemeMode = (): ThemeModeContextValue => {
    const ctx = useContext(ThemeModeContext);
    if (!ctx) {
        throw new Error('useThemeMode must be used within a ThemeModeProvider');
    }
    return ctx;
};
